import {
    checkAdminCatalogOfferSku,
    createAdminCatalogGroup,
    createAdminCatalogOffer,
    deleteAdminCatalogOffer,
    getAdminCatalogGroupVariationById,
    getKeycrmStocks,
    patchAdminCatalogGroup,
    patchAdminCatalogOffer,
    patchAdminCatalogOfferAccessories,
    patchAdminCatalogOfferStocks,
} from '../../../shared/api/products.services';
import toast from '../../../shared/lib/toast';
import {
    areOfferAccessoriesEqual,
    buildOfferAccessoriesPayload,
    buildOfferOptionKey,
    normalizeAccessoryGroupId,
} from './productFormHelpers';

const MAX_UNIQUENESS_ATTEMPTS = 20;

// Резервний id основного складу (KYIV_MAIN, isDefault) — лише фолбек, основне
// джерело id складу — defaultWarehouseId з відповіді сервера.
const DEFAULT_STOCK_WAREHOUSE_ID = '69e788239448d4a5e734305a';

// Один батч-запит залишків з KeyCRM за артикулами нових оферів.
// Повертає { items, warehouseId }.
async function buildKeycrmStockContext(offers = []) {
    const skus = [...new Set(
        (offers || []).map((o) => String(o?.sku || '').trim()).filter(Boolean)
    )];
    if (!skus.length) return { items: {}, warehouseId: DEFAULT_STOCK_WAREHOUSE_ID };
    try {
        const res = await getKeycrmStocks(skus);
        return {
            items: (res && res.items) || {},
            warehouseId: (res && res.defaultWarehouseId) || DEFAULT_STOCK_WAREHOUSE_ID,
        };
    } catch {
        return { items: {}, warehouseId: DEFAULT_STOCK_WAREHOUSE_ID };
    }
}

// Проставляє оферу залишок з KeyCRM (на основний склад). Якщо SKU не знайдено —
// лишає як є (дефолтний/введений залишок).
function applyKeycrmStockToOffer(offer, ctx) {
    const sku = String(offer?.sku || '').trim();
    const hit = sku ? ctx.items[sku] : null;
    if (!hit) return offer;
    return {
        ...offer,
        stocks: [{ warehouseId: ctx.warehouseId, onHand: hit.quantity || 0, reserved: hit.reserve || 0 }],
    };
}

// Чи збігається залишок офера на основному складі з тим, що повернув KeyCRM.
function offerStockMatchesKeycrm(offer, hit, warehouseId) {
    const cur = (Array.isArray(offer?.stocks) ? offer.stocks : []).find(
        (s) => String(s?.warehouseId?._id || s?.warehouseId || '') === String(warehouseId)
    );
    return (Number(cur?.onHand) || 0) === (hit.quantity || 0)
        && (Number(cur?.reserved) || 0) === (hit.reserve || 0);
}

// Patch-и залишків для ВЖЕ створених оферів: лише ті, де KeyCRM повернув дані й
// вони відрізняються від поточних (щоб не робити зайвих записів на кожному збереженні).
// Залишок не входить у звичайний update-payload офера, тож оновлюємо окремим патчем.
function buildExistingStockPatches(offers = [], ctx) {
    const patches = [];
    for (const offer of offers) {
        if (!offer?._id) continue;
        const sku = String(offer?.sku || '').trim();
        const hit = sku ? ctx.items[sku] : null;
        if (!hit) continue;
        if (offerStockMatchesKeycrm(offer, hit, ctx.warehouseId)) continue;
        patches.push({
            offerId: offer._id,
            stocks: [{ warehouseId: ctx.warehouseId, onHand: hit.quantity || 0, reserved: hit.reserve || 0 }],
        });
    }
    return patches;
}

// Pre-save SKU guard: blocks the whole save (before any create/patch, so no partial
// saves) if any offer's SKU duplicates another offer in the form or in another product.
async function findOfferSkuConflict(offers = []) {
    const list = (offers || []).map((offer, index) => ({
        index,
        sku: String(offer?.sku || '').trim(),
        offerId: offer?._id || null,
    }));

    const seen = new Set();
    for (const item of list) {
        if (!item.sku) continue;
        if (seen.has(item.sku)) {
            return { type: 'in-form', sku: item.sku };
        }
        seen.add(item.sku);
    }

    const checks = await Promise.all(
        list
            .filter((item) => item.sku)
            .map(async (item) => {
                const res = await checkAdminCatalogOfferSku(item.sku, item.offerId);
                return res?.duplicate ? { type: 'cross', sku: item.sku, conflict: res.conflict } : null;
            })
    );
    return checks.find(Boolean) || null;
}

// Бекенд тепер дозволяє зберегти оффер з незаповненою віссю варіації і
// повертає `warnings` (де саме і що не заповнено) у `response.item.warnings`.
const extractOfferWarnings = (response) => {
    const item = response?.item || response;
    const list = item?.warnings || response?.warnings;
    return Array.isArray(list) ? list : [];
};

const formatWarningLines = (warnings) => {
    const seen = new Set();
    const lines = [];
    for (const w of warnings) {
        const key = `${w?.sku || ''}|${w?.axisId || ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const axis = w?.axisTitle?.ua || w?.axisTitle?.en || w?.axisId || 'вісь';
        lines.push(`• ${w?.sku || 'оффер'}: «${axis}»`);
    }
    return lines;
};

const notifyWarnings = (warnings) => {
    const lines = formatWarningLines(warnings);
    if (!lines.length) return;
    const shown = lines.slice(0, 8);
    const extra = lines.length > 8 ? `\n…та ще ${lines.length - 8}` : '';
    toast.warning(`Збережено, але не заповнено осі варіацій:\n${shown.join('\n')}${extra}`);
};

const isUniquenessCollisionError = (error) => {
    // Бэкенд теперь отдаёт машинный code (SLUG_DUPLICATE / SKU_DUPLICATE), а
    // сообщение локализовано — поэтому опираемся на code, а текстовую проверку
    // оставляем как фолбэк (raw Mongo "duplicate key" и т.п.).
    const code = String(error?.code || error?.response?.data?.code || '').toUpperCase();
    if (code === 'SLUG_DUPLICATE' || code === 'SKU_DUPLICATE') return true;

    const message = String(error?.message || '').toLowerCase();
    return (
        message.includes('slug already exists') ||
        message.includes('slug exists') ||
        message.includes('sku already exists') ||
        message.includes('sku exists') ||
        message.includes('duplicate key')
    );
};

const withNumericSuffix = (value, attemptIndex) => {
    const base = String(value || '').trim();
    if (!base) return base;

    const cleanedBase = base.replace(/-\d+$/, '');
    return `${cleanedBase}-${attemptIndex + 2}`;
};

const createGroupWithUniqueSlug = async (payload) => {
    let lastError = null;

    for (let attempt = 0; attempt < MAX_UNIQUENESS_ATTEMPTS; attempt += 1) {
        const nextPayload = {
            ...payload,
            slug: attempt === 0 ? payload.slug : withNumericSuffix(payload.slug, attempt - 1),
        };

        try {
            return await createAdminCatalogGroup(nextPayload);
        } catch (error) {
            lastError = error;
            if (!isUniquenessCollisionError(error)) {
                throw error;
            }
        }
    }

    throw lastError || new Error('Не вдалося згенерувати унікальний slug');
};

const createOfferWithUniqueFields = async (groupId, payload) => {
    let lastError = null;

    for (let attempt = 0; attempt < MAX_UNIQUENESS_ATTEMPTS; attempt += 1) {
        const nextPayload = { ...payload };

        if (attempt > 0) {
            if (nextPayload.slug) {
                nextPayload.slug = withNumericSuffix(nextPayload.slug, attempt - 1);
            }
            if (nextPayload.sku) {
                nextPayload.sku = withNumericSuffix(nextPayload.sku, attempt - 1);
            }
        }

        try {
            return await createAdminCatalogOffer(groupId, nextPayload);
        } catch (error) {
            lastError = error;
            if (!isUniquenessCollisionError(error)) {
                throw error;
            }
        }
    }

    throw lastError || new Error('Не вдалося створити оффер через конфлікт slug/sku');
};

export async function handleProductFormSubmit({
    type,
    values,
    initialData,
    variationsData,
    navigate,
    formatPayload,
    buildGroupPatchPayload,
    buildOfferCreatePayload,
    buildOfferUpdatePayload,
    areOffersEqual,
}) {
    const collectedWarnings = [];
    try {
        const skuConflict = await findOfferSkuConflict(values.offers);
        if (skuConflict) {
            if (skuConflict.type === 'in-form') {
                toast.error(`Артикул «${skuConflict.sku}» повторюється у двох варіантах цього товару. Змініть його.`);
            } else {
                const where = skuConflict.conflict?.groupTitle
                    ? `у товарі «${skuConflict.conflict.groupTitle}»`
                    : 'в іншому товарі';
                toast.error(`Артикул «${skuConflict.sku}» вже використовується ${where}. Змініть артикул.`);
            }
            return;
        }

        if (type === 'create') {
            // Авто-підтягування залишків з KeyCRM за артикулом для всіх нових оферів.
            const stockCtx = await buildKeycrmStockContext(values.offers || []);
            const valuesWithStock = {
                ...values,
                offers: (values.offers || []).map((o) => applyKeycrmStockToOffer(o, stockCtx)),
            };
            const payload = formatPayload(valuesWithStock);
            const createdGroupResponse = await createGroupWithUniqueSlug(payload);
            collectedWarnings.push(...extractOfferWarnings(createdGroupResponse));

            const createdGroupId = String(createdGroupResponse?.item?._id || createdGroupResponse?._id || '').trim();
            const offersWithAccessory = (values.offers || []).filter((offer) => normalizeAccessoryGroupId(offer));

            if (createdGroupId && offersWithAccessory.length > 0) {
                const offersResult = await getAdminCatalogGroupVariationById(createdGroupId, {
                    page: 1,
                    limit: Math.max((values.offers || []).length || 1, 100),
                });

                const createdOffers = offersResult?.items || offersResult?.item?.offers || [];
                const createdOfferIdByOptionKey = new Map(
                    createdOffers.map((offer) => [buildOfferOptionKey(offer), String(offer?._id || '')])
                );

                await Promise.all(
                    offersWithAccessory.map((offer) => {
                        const createdOfferId = createdOfferIdByOptionKey.get(buildOfferOptionKey(offer));
                        if (!createdOfferId) return Promise.resolve();

                        return patchAdminCatalogOfferAccessories(
                            createdOfferId,
                            buildOfferAccessoriesPayload(offer)
                        );
                    })
                );
            }

            toast.success('Товар успішно створено!');
        } else {
            const groupId = initialData?._id;
            const initialOffers = variationsData?.items || [];
            const nextOffers = values.offers || [];

            const nextOfferIds = new Set(
                nextOffers
                    .filter((offer) => offer?._id)
                    .map((offer) => String(offer._id))
            );

            const offersToDelete = initialOffers.filter(
                (offer) => offer?._id && !nextOfferIds.has(String(offer._id))
            );

            const initialOffersById = new Map(
                initialOffers
                    .filter((offer) => offer?._id)
                    .map((offer) => [String(offer._id), offer])
            );

            const offersToUpdate = nextOffers.filter((offer) => {
                if (!offer?._id) return false;
                const initialOffer = initialOffersById.get(String(offer._id));
                if (!initialOffer) return true;
                return !areOffersEqual(offer, initialOffer);
            });

            const offersToCreate = nextOffers.filter((offer) => !offer?._id);

            // Авто-підтягування залишків з KeyCRM за артикулом для ВСІХ оферів
            // (один батч-запит): нові — у create-payload, існуючі — окремим patch
            // стоків нижче.
            const stockCtx = await buildKeycrmStockContext(nextOffers);

            await patchAdminCatalogGroup(groupId, buildGroupPatchPayload(values));
            await Promise.all(offersToDelete.map((offer) => deleteAdminCatalogOffer(offer._id)));
            const updatedOfferResponses = await Promise.all(
                offersToUpdate.map((offer) =>
                    patchAdminCatalogOffer(offer._id, buildOfferUpdatePayload(offer))
                )
            );
            const createdOfferResponses = await Promise.all(
                offersToCreate.map((offer) =>
                    createOfferWithUniqueFields(groupId, buildOfferCreatePayload(applyKeycrmStockToOffer(offer, stockCtx)))
                )
            );

            // Існуючі офери: підтягуємо залишок з KeyCRM (якщо прийшов і змінився).
            await Promise.all(
                buildExistingStockPatches(nextOffers, stockCtx).map(({ offerId, stocks }) =>
                    patchAdminCatalogOfferStocks(offerId, stocks).catch((e) => {
                        console.error('stock patch failed', e);
                        return null;
                    })
                )
            );

            updatedOfferResponses.forEach((r) => collectedWarnings.push(...extractOfferWarnings(r)));
            createdOfferResponses.forEach((r) => collectedWarnings.push(...extractOfferWarnings(r)));

            const accessoryPatches = [];

            nextOffers.forEach((offer) => {
                if (!offer?._id) return;

                const initialOffer = initialOffersById.get(String(offer._id));
                if (initialOffer && areOfferAccessoriesEqual(offer, initialOffer)) {
                    return;
                }

                accessoryPatches.push({
                    offerId: offer._id,
                    payload: buildOfferAccessoriesPayload(offer),
                });
            });

            createdOfferResponses.forEach((response, index) => {
                const createdOfferId = String(response?.item?._id || response?._id || '').trim();
                const createdOfferDraft = offersToCreate[index];

                if (!createdOfferId || !normalizeAccessoryGroupId(createdOfferDraft)) {
                    return;
                }

                accessoryPatches.push({
                    offerId: createdOfferId,
                    payload: buildOfferAccessoriesPayload(createdOfferDraft),
                });
            });

            await Promise.all(
                accessoryPatches.map(({ offerId, payload }) =>
                    patchAdminCatalogOfferAccessories(offerId, payload)
                )
            );

            toast.success('Товар успішно оновлено!');
        }

        notifyWarnings(collectedWarnings);
        navigate('/products');

    } catch (error) {
        console.error('Помилка при збереженні:', error);
        toast.error(error.message || 'Сталася помилка. Спробуйте ще раз.');
    }
}
